import React, { useState, useEffect, useRef } from 'react';
import { Heart, X, Sparkles, MapPin, Crown, Diamond, Edit2, Plus, Camera, Trophy, Wand2, MessageCircle, ArrowLeft, Star, Ban, ShieldOff, ChevronRight, Flag } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { RankBadge } from '../components/RankBadge';
import AvatarPicker from '../components/AvatarPicker';
import ChatComposer from '../components/ChatComposer';
import GiftFly from '../components/GiftFly';
import DateInviteCard from '../components/DateInviteCard';
import ReportUserDialog from '../components/ReportUserDialog';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ws, { useChannel } from '../lib/ws';

const INTERESTS = ['Music', 'Travel', 'Gaming', 'Anime', 'Movies', 'Reading', 'Coffee', 'Food', 'Sports', 'Art', 'Tech', 'Photography', 'Fitness', 'Cooking', 'Pets', 'Dance', 'Yoga', 'Hiking'];
const GENDERS = ['Female', 'Male', 'Non-binary', 'Other'];
const LOOKING_FOR = ['Friendship', 'Dating', 'Relationship', 'Marriage'];
const DATE_INVITE_PRESETS = [
  { kind: 'Coffee', emoji: '☕' },
  { kind: 'Walk', emoji: '🚶' },
  { kind: 'Movie', emoji: '🎬' },
  { kind: 'Dinner', emoji: '🍽️' },
];

// 18+ gate — dating routes are server-side blocked for minors. This
// wrapper short-circuits before the (huge) Dating component's hooks run
// so a minor never fires the /api/dating requests at all. Extracted to a
// separate function so it doesn't violate the rules of hooks.
function DatingUnder18Notice() {
  return (
    <div className="max-w-lg mx-auto pt-16 px-6 text-center">
      <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-rose-500/25 to-fuchsia-500/10 border border-rose-400/30 flex items-center justify-center mb-5">
        <Heart size={36} className="text-rose-300" />
      </div>
      <h1 className="font-display font-bold text-2xl">Dating unlocks at 18</h1>
      <p className="mt-3 text-white/70 text-sm leading-relaxed" data-testid="dating-under-18-notice">
        WorldVibe&apos;s Match Hearts area — swiping, proposals, and dating chats — is for legal adults only.
        Everything else on WorldVibe (planets, chat, feed, gifts, games, World Quest) is fully open to you.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/app/planets">
          <Button className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90">Explore Planets instead</Button>
        </Link>
        <Link to="/app/games">
          <Button variant="outline" className="w-full border-white/15 bg-white/5">Play Games</Button>
        </Link>
      </div>
    </div>
  );
}

// Verify Age card — shown to OAuth users (Google/Apple) who never gave us
// a birthdate at signup. Lets them unlock Dating with a one-time entry
// instead of blocking every login. Once set, DOB is immutable server-side.
function DatingAgeVerifyCard() {
  const { refresh } = useAuth();
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const TODAY_ISO = new Date().toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!dob) { setError('Please enter your birthdate'); return; }
    setError(''); setBusy(true);
    try {
      await api.post('/auth/set-birthdate', { date_of_birth: dob });
      toast.success('Age verified — welcome to Dating ✨');
      refresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not verify age');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto pt-16 px-6 text-center">
      <div className="w-20 h-20 mx-auto rounded-3xl bg-gradient-to-br from-fuchsia-500/25 to-cyan-500/10 border border-fuchsia-400/30 flex items-center justify-center mb-5">
        <Heart size={36} className="text-fuchsia-300" />
      </div>
      <h1 className="font-display font-bold text-2xl">One step to unlock Dating</h1>
      <p className="mt-3 text-white/70 text-sm leading-relaxed" data-testid="dating-age-verify-notice">
        Match Hearts is for travelers <b className="text-white">18 and above</b>. Add your birthdate once
        to unlock swiping, proposals and dating chats. We store it privately and never show it on your profile.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-3 text-left">
        <label className="block text-xs uppercase tracking-wider text-white/50">Your birthdate</label>
        <Input
          type="date"
          value={dob}
          onChange={(e) => setDob(e.target.value)}
          max={TODAY_ISO}
          disabled={busy}
          required
          data-testid="dating-age-verify-input"
          className="bg-white/5 border-white/10 text-white"
        />
        {error && (
          <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded-lg px-3 py-2" data-testid="dating-age-verify-error">
            {error}
          </div>
        )}
        <Button
          type="submit"
          disabled={busy || !dob}
          data-testid="dating-age-verify-submit"
          className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90"
        >
          {busy ? 'Verifying…' : 'Unlock Dating'}
        </Button>
      </form>
      <div className="mt-3 text-[10px] text-white/40">Once set, your birthdate cannot be changed.</div>
    </div>
  );
}


// Public export — gates on `is_adult` and renders the notice screen for
// under-18 users without instantiating the full DatingInner component.
// OAuth users who never gave a DOB see a one-time age-verify card instead
// of being permanently blocked.
export default function Dating() {
  const { user } = useAuth();
  if (user && user.is_adult === false) {
    if (!user.date_of_birth) return <DatingAgeVerifyCard />;
    return <DatingUnder18Notice />;
  }
  return <DatingInner />;
}


function DatingInner() {
  const { user, updateProfile, refresh, openBuyStars, creditStars } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [idx, setIdx] = useState(0);
  const [matches, setMatches] = useState([]);
  const [likedIds, setLikedIds] = useState(() => new Set(JSON.parse(localStorage.getItem('wv_liked') || '[]')));
  const [relationship, setRelationship] = useState(() => JSON.parse(localStorage.getItem('wv_relationship') || 'null'));
  const [dialog, setDialog] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [chatMatch, setChatMatch] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatThread, setChatThread] = useState(null);   // {status, blocked_by_me, blocked_by_other, ...}
  const [gifts, setGifts] = useState([]);
  const [fly, setFly] = useState(null);
  const chatScrollRef = useRef(null);
  const photoFileRef = useRef(null);

  // Resize-on-client → base64 JPEG (max 720px) — matches ChatComposer image helper
  const fileToDataUrl = (file, maxDim = 720) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      c.getContext('2d').drawImage(img, 0, 0, w, h);
      resolve(c.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = URL.createObjectURL(file);
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please choose an image file (PNG/JPG/WebP)'); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error('Image too large (max 8MB). Please choose a smaller photo.'); return; }
    try {
      const dataUrl = await fileToDataUrl(file);
      setProfile(p => {
        if (p.photos.length >= 4) {
          toast.error('You can add up to 4 photos. Remove one first.');
          return p;
        }
        return { ...p, photos: [...p.photos, dataUrl] };
      });
      toast.success('Photo added to your dating profile — remember to Save profile ✨');
    } catch (err) {
      console.error('dating photo upload failed', err);
      toast.error(err.message || 'Could not load image — try a different photo.');
    } finally {
      if (photoFileRef.current) photoFileRef.current.value = '';
    }
  };

  // Load real matches from the backend + the gift catalog
  const loadMatches = async () => {
    try {
      const r = await api.get('/dating/matches');
      setMatches(r.data || []);
    } catch { /* ignore */ }
  };

  // Load incoming requests: likes (not yet matched) + pending proposals from others.
  // Excludes anything I sent — that goes in "outgoing" below.
  const [incomingLikes, setIncomingLikes] = useState([]);
  const [incomingProposals, setIncomingProposals] = useState([]);
  const [outgoingProposals, setOutgoingProposals] = useState([]);
  const [outgoingLikes, setOutgoingLikes] = useState([]);  // likes I sent, awaiting their reply

  const loadRequests = async () => {
    try {
      const [likesRes, inRes, outRes, outLikesRes] = await Promise.all([
        api.get('/dating/likes/incoming'),
        api.get('/dating/proposals/incoming'),
        api.get('/dating/proposals/outgoing'),
        api.get('/dating/likes/outgoing'),
      ]);
      setIncomingLikes(likesRes.data || []);
      setIncomingProposals(inRes.data || []);
      setOutgoingProposals(outRes.data || []);
      setOutgoingLikes(outLikesRes.data || []);
    } catch { /* ignore */ }
  };

  // Quick lookups so the UI can hide propose buttons for users I've already proposed to
  const pendingOutgoingByUser = React.useMemo(() => {
    const map = {};
    (outgoingProposals || []).filter(p => p.status === 'pending').forEach(p => { map[p.to_user] = p; });
    return map;
  }, [outgoingProposals]);

  // Like-back an incoming like → calls the same like endpoint (returns matched:true)
  const likeBack = async (u) => {
    try {
      const { data } = await api.post(`/dating/like/${u.user_id}`);
      toast.success(data?.matched ? `It's a match with ${u.displayName} 💕` : `Liked back!`);
      setIncomingLikes(prev => prev.filter(x => x.user_id !== u.user_id));
      await loadMatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not like'); }
  };

  const passLike = async (u) => {
    try {
      await api.post(`/dating/likes/${u.user_id}/dismiss`);
      setIncomingLikes(prev => prev.filter(x => x.user_id !== u.user_id));
      toast(`Not for me — showing next profile ✨`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not pass'); }
  };

  // Send a proposal (date / engaged / married) to a user — real backend call
  const sendProposal = async (u, kind = 'date', message = null) => {
    try {
      const { data } = await api.post(`/dating/propose/${u.user_id}`, { kind, message });
      toast.success(`${kind === 'date' ? '💕' : kind === 'engaged' ? '💍' : '💒'} Proposal sent to ${u.displayName}`);
      setDialog(null);
      // Remember the proposal locally so this user's card hides the propose button
      setOutgoingProposals(prev => [data, ...prev.filter(p => !(p.to_user === u.user_id && p.status === 'pending'))]);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send proposal'); }
  };

  const respondProposal = async (prop, action) => {
    try {
      const { data } = await api.post(`/dating/proposals/${prop.id}/respond`, { action });
      // Remove the responded item from the list so the user can see the OTHER requests
      setIncomingProposals(prev => prev.filter(p => p.id !== prop.id));
      toast.success(action === 'accept'
        ? `💕 You accepted ${prop.partner?.displayName || 'their'} proposal`
        : `Declined ${prop.partner?.displayName || ''}`);
      if (action === 'accept' && data?.kind === 'engaged') { persistRelationship({ partner: prop.partner, status: 'engaged', since: Date.now() }); }
      if (action === 'accept' && data?.kind === 'married') { persistRelationship({ partner: prop.partner, status: 'married', since: Date.now() }); }
      if (action === 'accept' && data?.kind === 'date')     { persistRelationship({ partner: prop.partner, status: 'dating',  since: Date.now() }); }
      await loadMatches();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not respond'); }
  };

  useEffect(() => {
    loadMatches();
    loadRequests();
    api.get('/gifts').then(r => setGifts(r.data || [])).catch(() => {});
    const t = setInterval(loadRequests, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!chatMatch?.user_id) return;
    let alive = true;
    setChatLoading(true);
    api.get(`/dms/${chatMatch.user_id}/messages`)
      .then(r => { if (alive) setChatMessages(r.data || []); })
      .catch(() => toast.error('Could not load chat'))
      .finally(() => alive && setChatLoading(false));
    // Fetch thread meta so we know if either side has blocked
    api.get(`/dms/${chatMatch.user_id}/thread`)
      .then(r => { if (alive) setChatThread(r.data); })
      .catch(() => { if (alive) setChatThread(null); });
    return () => { alive = false; };
  }, [chatMatch?.user_id]);

  const reloadChatThread = () => {
    if (!chatMatch?.user_id) return;
    api.get(`/dms/${chatMatch.user_id}/thread`).then(r => setChatThread(r.data)).catch(() => {});
  };

  const blockChatMatch = async () => {
    if (!chatMatch?.user_id) return;
    if (!window.confirm(`Block ${chatMatch.displayName}? You won't be able to message each other. You can unblock them anytime.`)) return;
    try {
      await api.post(`/dms/${chatMatch.user_id}/block`);
      toast.success(`${chatMatch.displayName} blocked`);
      reloadChatThread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not block');
    }
  };

  const unblockChatMatch = async () => {
    if (!chatMatch?.user_id) return;
    try {
      await api.post(`/dms/${chatMatch.user_id}/unblock`);
      toast.success(`${chatMatch.displayName} unblocked — you can message again`);
      reloadChatThread();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not unblock');
    }
  };

  // Live: append/update messages for the dating chat thread via WebSocket
  const chatThreadKey = user && chatMatch ? ws.dmThreadKey(user.user_id, chatMatch.user_id) : null;
  useChannel(chatThreadKey ? `dm:${chatThreadKey}` : null, (evt) => {
    if (!evt.message) return;
    if (evt.type === 'dm.message') {
      setChatMessages(prev => prev.some(m => m.id === evt.message.id) ? prev : [...prev, evt.message]);
    } else if (evt.type === 'dm.message.update') {
      setChatMessages(prev => prev.map(m => m.id === evt.message.id ? { ...m, ...evt.message } : m));
    }
  }, [chatThreadKey]);

  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatMessages.length]);

  const sendChat = async (body, extras = {}) => {
    if (!chatMatch?.user_id) return;
    try {
      const payload = { text: body };
      if (extras.image_url) payload.image_url = extras.image_url;
      const { data } = await api.post(`/dms/${chatMatch.user_id}/messages`, payload);
      setChatMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not send message';
      toast.error(msg);
      // Refresh block state so the UI reflects the current constraint
      if (e?.response?.status === 403) reloadChatThread();
      // Re-throw so ChatComposer suppresses its own success toast on failed image upload
      throw e;
    }
  };

  const sendGift = async (gift) => {
    if (!chatMatch?.user_id) return;
    if ((user?.stars ?? 0) < gift.price) {
      const need = gift.price - (user?.stars ?? 0);
      toast.error(`Need ${need.toLocaleString()} more ⭐ for ${gift.name}. Top up to send it!`);
      openBuyStars();
      throw new Error('insufficient_stars');
    }
    try {
      await api.post('/gifts/send', { gift_id: gift.id, recipient_id: chatMatch.user_id });
      creditStars(-gift.price);
      setFly({ emoji: gift.emoji, name: `${gift.name} → ${chatMatch.displayName}` });
      toast.success(`Sent ${gift.name} ${gift.emoji}`);
      // WS delivers the inline gift bubble — skip the refetch
      refresh?.();
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (e.response?.status === 400 && /not enough stars/i.test(detail)) {
        toast.error('Not enough stars — open Buy Stars to top up');
        openBuyStars();
        throw e;
      }
      toast.error(detail || 'Could not send gift');
      throw e;
    }
  };

  const sendDateInvite = async (payload) => {
    if (!chatMatch?.user_id) return;
    try {
      const { data } = await api.post(`/dms/${chatMatch.user_id}/date-invite`, payload);
      setChatMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
      toast.success(`${payload.emoji} ${payload.kind} invite sent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send invite');
      throw e;
    }
  };

  const updateInviteMsg = (updated) => {
    setChatMessages(prev => prev.map(m => m.id === updated.id ? { ...m, ...updated } : m));
  };

  // Local-state profile for dating. NOTE: dating photos are a SEPARATE list from
  // the account's profile picture (user.avatar). We do NOT seed with the avatar
  // — that made users think "photos here == profile picture" and led to
  // reports that uploading in Dating was overwriting their profile pic.
  const [profile, setProfile] = useState({
    gender: user?.gender || '',
    age: user?.age || 25,
    looking_for: user?.looking_for || 'Dating',
    interests: user?.interests || [],
    photos: Array.isArray(user?.photos) ? user.photos : [],
    bio: user?.bio || '',
    dating_active: user?.dating_active ?? true,
  });

  // Keep local state in sync when the auth user hydrates asynchronously
  // (e.g. after a fresh reload the /auth/me response lands AFTER first render).
  useEffect(() => {
    if (!user) return;
    setProfile(prev => ({
      ...prev,
      gender: user.gender ?? prev.gender,
      age: user.age ?? prev.age,
      looking_for: user.looking_for ?? prev.looking_for,
      interests: user.interests ?? prev.interests,
      photos: Array.isArray(user.photos) ? user.photos : prev.photos,
      bio: user.bio ?? prev.bio,
      dating_active: user.dating_active ?? prev.dating_active,
    }));
  }, [user?.user_id, user?.photos, user?.avatar]);

  const profileComplete = !!(profile.gender && profile.age && profile.looking_for && profile.interests?.length >= 1);

  useEffect(() => {
    api.get('/users/online').then(r => setCandidates(r.data.filter(u => u.user_id !== user?.user_id))).catch(() => {});
  }, [user]);

  const persistLiked = (set) => { localStorage.setItem('wv_liked', JSON.stringify([...set])); setLikedIds(new Set(set)); };
  const persistRelationship = (n) => { localStorage.setItem('wv_relationship', JSON.stringify(n)); setRelationship(n); };

  const current = candidates[idx];
  const skip = () => setIdx(i => Math.min(i + 1, candidates.length - 1));
  const like = async () => {
    if (!current) return;
    try {
      const { data } = await api.post(`/dating/like/${current.user_id}`);
      const updated = new Set(likedIds); updated.add(current.user_id); persistLiked(updated);
      if (data?.matched) {
        toast.success(`It's a match with ${current.displayName} 💕`);
        await loadMatches();
      } else {
        toast(`You liked ${current.displayName} — waiting for them to like you back ✨`);
      }
      // Refresh "awaiting their reply" list so the new like shows up immediately
      loadRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send like');
    }
    skip();
  };

  // Legacy local-only propose helper — kept only for the in-Discover quick "Propose" diamond button.
  // The REAL proposal flow now goes through sendProposal() above which hits the backend.
  const propose = (u) => sendProposal(u, 'date');
  const engage = () => { persistRelationship({ ...relationship, status: 'engaged', since: Date.now() }); toast.success(`Engaged 💍`); };
  const marry = () => { persistRelationship({ ...relationship, status: 'married', since: Date.now() }); toast.success(`Married 💍✨`); };
  const divorce = () => { toast(`Bond ended`); persistRelationship(null); };

  const toggleInterest = (i) => setProfile(p => ({ ...p, interests: p.interests.includes(i) ? p.interests.filter(x => x !== i) : [...p.interests, i] }));

  const saveProfile = async () => {
    const res = await updateProfile({
      gender: profile.gender || null,
      age: parseInt(profile.age, 10) || null,
      looking_for: profile.looking_for || null,
      interests: profile.interests,
      photos: profile.photos,
      bio: profile.bio,
      dating_active: profile.dating_active,
    });
    if (res.ok) { toast.success('Dating profile updated ✨'); setEditOpen(false); }
    else toast.error(res.error || 'Could not save');
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl md:text-3xl">Dating</h1>
          <p className="text-white/60 text-sm mt-1">Discover. Propose. Engage. Marry.</p>
        </div>
        <Button onClick={() => setEditOpen(true)} variant="outline" className="border-fuchsia-400/30 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 text-white rounded-full">
          <Edit2 size={14} className="mr-1" /> My Dating Profile
        </Button>
      </div>

      {/* Profile completion banner */}
      {!profileComplete && (
        <Card className="bg-gradient-to-br from-amber-500/15 to-rose-500/10 border-amber-400/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Sparkles className="text-amber-300" size={22} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Complete your dating profile</div>
              <div className="text-xs text-white/60">Add gender, age, what you&apos;re looking for and at least 1 interest to maximize matches.</div>
            </div>
            <Button size="sm" onClick={() => setEditOpen(true)} className="bg-amber-400 hover:bg-amber-500 text-black border-0 rounded-full font-semibold">Set up</Button>
          </CardContent>
        </Card>
      )}

      {relationship && (
        <Card className="bg-[#161622] border-rose-400/30">
          <CardContent className="p-4 flex items-center gap-3">
            <Avatar className="w-12 h-12 ring-2 ring-rose-400/60"><AvatarImage src={relationship.partner.avatar} /><AvatarFallback>{relationship.partner.displayName?.slice(0,2)}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm flex items-center gap-1.5">
                {relationship.status === 'married' ? <Crown size={14} className="text-amber-300" /> : relationship.status === 'engaged' ? <Diamond size={14} className="text-cyan-300" /> : <Heart size={14} className="text-rose-300" />}
                {relationship.status[0].toUpperCase() + relationship.status.slice(1)} with {relationship.partner.displayName}
              </div>
              <div className="text-[11px] text-white/50 mt-0.5">Since {new Date(relationship.since).toLocaleDateString()}</div>
            </div>
            <div className="flex gap-1.5">
              {relationship.status === 'proposed' && <Button size="sm" onClick={engage} className="bg-cyan-500 hover:bg-cyan-600 text-white border-0">Accept</Button>}
              {relationship.status === 'engaged' && <Button size="sm" onClick={marry} className="bg-amber-500 hover:bg-amber-600 text-black border-0">Marry</Button>}
              <Button size="sm" onClick={divorce} variant="outline" className="border-white/15 bg-white/5 hover:bg-rose-500/20 text-white">End</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="discover">
        <TabsList className="bg-[#161622] border border-white/10">
          <TabsTrigger value="discover" className="data-[state=active]:bg-white/10">Discover</TabsTrigger>
          <TabsTrigger value="requests" data-testid="dating-tab-requests" className="data-[state=active]:bg-white/10">
            Requests {(incomingLikes.length + incomingProposals.length) > 0 && <span className="ml-1.5 text-[10px] bg-rose-500 px-1.5 rounded-full">{incomingLikes.length + incomingProposals.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="matches" className="data-[state=active]:bg-white/10">Matches ({matches.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="discover" className="mt-5">
          {current ? (
            <Card className="bg-[#161622] border-white/10 overflow-hidden max-w-md mx-auto" data-testid="dating-discover-card">
              <div className="relative h-40 sm:h-64" style={{ background: 'linear-gradient(135deg, rgba(244,114,182,0.35), rgba(123,47,247,0.25), rgba(0,198,255,0.2))' }}>
                <Avatar className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-36 sm:h-36 ring-4 ring-white/30"><AvatarImage src={(current.photos && current.photos[0]) || current.avatar} /><AvatarFallback className="text-3xl sm:text-4xl">{current.displayName?.slice(0,2)}</AvatarFallback></Avatar>
                <div className="absolute top-3 right-3 text-2xl">{current.flag}</div>
                {current.rank && <div className="absolute top-3 left-3"><RankBadge rank={current.rank} /></div>}
              </div>
              <CardContent className="p-3 sm:p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-display font-bold text-lg sm:text-xl">{current.displayName}{current.age ? `, ${current.age}` : ''}</h2>
                </div>
                <div className="text-white/60 text-xs flex items-center gap-3 mt-1">
                  <span className="flex items-center gap-1"><MapPin size={11} /> {current.country}</span>
                  {current.gender && <span>· {current.gender}</span>}
                </div>
                {current.bio && <p className="text-sm text-white/70 mt-2 line-clamp-2 sm:line-clamp-3">{current.bio}</p>}
                {current.interests?.length > 0 && (
                  <div className="mt-2 hidden sm:flex flex-wrap gap-1.5">
                    {current.interests.slice(0, 6).map(i => <Badge key={i} className="bg-white/5 border-white/10 text-white/80 hover:bg-white/10">{i}</Badge>)}
                  </div>
                )}
                <div className="flex items-center justify-center gap-5 sm:gap-6 mt-3 sm:mt-5" data-testid="dating-action-buttons">
                  <button onClick={skip} data-testid="dating-skip-btn" aria-label="Next profile" title="Not for me — show next profile" className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center border border-white/15"><ChevronRight size={22} className="text-white/70" /></button>
                  <button onClick={() => setDialog({ kind: 'report', user: current })} data-testid="dating-report-btn" aria-label="Report this profile" title="Report this profile" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/5 hover:bg-rose-500/20 flex items-center justify-center border border-white/15 hover:border-rose-400/40 transition"><Flag size={15} className="text-white/70" /></button>
                  <button onClick={() => setDialog({ kind: 'propose', user: current })} data-testid="dating-propose-btn" aria-label="Propose" className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-amber-400/20 hover:bg-amber-400/30 flex items-center justify-center border border-amber-400/40"><Diamond size={16} className="text-amber-300 sm:hidden" /><Diamond size={18} className="text-amber-300 hidden sm:block" /></button>
                  <button onClick={like} data-testid="dating-like-btn" aria-label="Like" className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 hover:scale-110 flex items-center justify-center shadow-lg shadow-rose-500/30 transition-transform"><Heart size={24} fill="white" className="text-white sm:hidden" /><Heart size={28} fill="white" className="text-white hidden sm:block" /></button>
                </div>
                <p className="text-[10px] text-white/40 text-center mt-3 leading-relaxed">
                  Be kind. Report anything that feels off — moderators review within 24 hours.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-16 text-white/40">No more profiles — check back soon ✨</div>
          )}
        </TabsContent>

        <TabsContent value="requests" className="mt-5" data-testid="dating-requests-pane">
          {(incomingLikes.length + incomingProposals.length) === 0 ? (
            <div className="text-center py-12 text-white/40">
              <Heart className="inline-block text-rose-300/40" size={36} />
              <div className="mt-3 text-sm">No incoming requests right now</div>
              <div className="text-[11px] text-white/30 mt-1">When someone likes you or proposes, you&apos;ll see them here.</div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Proposals first — they require a richer action */}
              {incomingProposals.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-300/80 mb-2 flex items-center gap-1">
                    <Diamond size={11} /> Proposals ({incomingProposals.length})
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {incomingProposals.map(p => {
                      const partner = p.partner || {};
                      const emoji = p.kind === 'engaged' ? '💍' : p.kind === 'married' ? '💒' : '💕';
                      return (
                        <Card key={p.id} className="bg-gradient-to-br from-rose-500/10 to-fuchsia-500/10 border-rose-400/30">
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="w-12 h-12 ring-2 ring-rose-400/40"><AvatarImage src={partner.avatar} /><AvatarFallback>{partner.displayName?.slice(0,2)}</AvatarFallback></Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate">{partner.displayName}</div>
                                <div className="text-[11px] text-rose-200 flex items-center gap-1">
                                  <span>{emoji}</span><span className="capitalize">{p.kind}</span> proposal
                                </div>
                              </div>
                            </div>
                            {p.message && <div className="mt-2 text-xs text-white/70 italic">&ldquo;{p.message}&rdquo;</div>}
                            <div className="mt-3 flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => respondProposal(p, 'accept')}
                                data-testid={`dating-prop-accept-${p.id}`}
                                className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-0 rounded-full h-8 text-xs font-semibold"
                              >
                                <Heart size={11} className="mr-1" fill="white" /> Accept
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => respondProposal(p, 'decline')}
                                data-testid={`dating-prop-decline-${p.id}`}
                                className="flex-1 border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-8 text-xs"
                              >
                                <X size={11} className="mr-1" /> Decline
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Incoming likes (someone liked me; if I like back, instant match) */}
              {incomingLikes.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-pink-300/80 mb-2 flex items-center gap-1">
                    <Heart size={11} /> Likes ({incomingLikes.length})
                  </div>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {incomingLikes.map(u => (
                      <Card key={u.user_id} className="bg-[#161622] border-pink-400/20">
                        <CardContent className="p-4 flex items-center gap-3">
                          <Avatar className="w-12 h-12 ring-2 ring-pink-400/40"><AvatarImage src={u.avatar} /><AvatarFallback>{u.displayName?.slice(0,2)}</AvatarFallback></Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{u.displayName}</div>
                            <div className="text-[11px] text-white/50">{u.country}</div>
                          </div>
                          <Button size="sm" onClick={() => likeBack(u)} data-testid={`dating-like-back-${u.user_id}`} className="bg-pink-500 hover:bg-pink-600 text-white border-0 rounded-full h-8 text-xs">
                            <Heart size={11} className="mr-1" fill="white" />Like back
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => passLike(u)} data-testid={`dating-pass-${u.user_id}`} className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full h-8 text-xs">
                            <X size={11} />
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="matches" className="mt-5" data-testid="dating-matches-pane">
          {/* Awaiting their like — visible reassurance that the user's like was sent */}
          {outgoingLikes.length > 0 && (
            <div className="mb-6">
              <div className="text-[10px] uppercase tracking-wider text-amber-200/80 mb-2 flex items-center gap-1">
                <Sparkles size={11} /> Awaiting their reply ({outgoingLikes.length})
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {outgoingLikes.map(u => (
                  <Card key={u.user_id} data-testid={`dating-awaiting-${u.user_id}`} className="bg-gradient-to-br from-amber-500/8 to-rose-500/8 border border-amber-400/25 relative overflow-hidden">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="relative">
                        <Avatar className="w-12 h-12 ring-2 ring-amber-400/40 opacity-80"><AvatarImage src={u.avatar} /><AvatarFallback>{u.displayName?.slice(0,2)}</AvatarFallback></Avatar>
                        <Heart size={11} className="absolute -bottom-0.5 -right-0.5 text-rose-300 fill-rose-400 drop-shadow" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{u.displayName}</div>
                        <div className="text-[11px] text-amber-200/70 flex items-center gap-1 mt-0.5">
                          <Sparkles size={9} className="text-amber-300" />
                          <span>You liked them — waiting for them to like back</span>
                        </div>
                      </div>
                      <div className="text-[9px] uppercase tracking-wider bg-amber-400/15 text-amber-200 px-2 py-1 rounded-full whitespace-nowrap">Pending</div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {matches.length === 0 && outgoingLikes.length === 0 ? (
            <div className="text-center py-12 text-white/40">Like profiles in Discover to start matching.</div>
          ) : matches.length === 0 ? (
            <div className="text-center py-8 text-white/40 text-sm">No mutual matches yet — but you have {outgoingLikes.length} pending {outgoingLikes.length === 1 ? 'like' : 'likes'} above ✨</div>
          ) : (
            <>
              {outgoingLikes.length > 0 && (
                <div className="text-[10px] uppercase tracking-wider text-rose-200/80 mb-2 flex items-center gap-1">
                  <Heart size={11} fill="currentColor" /> Mutual matches ({matches.length})
                </div>
              )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {matches.map(m => {
                const pendingProp = pendingOutgoingByUser[m.user_id];
                return (
                <Card key={m.user_id} className="bg-[#161622] border-rose-400/20">
                  <CardContent className="p-4 flex items-center gap-3">
                    <Avatar className="w-12 h-12 ring-2 ring-rose-400/50"><AvatarImage src={m.avatar} /><AvatarFallback>{m.displayName?.slice(0,2)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{m.displayName}</div>
                      <div className="text-[11px] text-white/50">{m.country}</div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => { setChatMatch(m); api.post(`/users/${m.user_id}/view`).catch(() => {}); }}
                      data-testid={`dating-chat-${m.user_id}`}
                      className="bg-rose-500 hover:bg-rose-600 text-white border-0 rounded-full h-8 text-xs font-semibold"
                    >
                      <MessageCircle size={12} className="mr-1" />Chat
                    </Button>
                    {pendingProp ? (
                      <Button size="sm" disabled data-testid={`dating-propose-pending-${m.user_id}`} className="bg-amber-500/20 text-amber-200 border border-amber-400/30 rounded-full h-8 text-[11px] font-semibold cursor-not-allowed">
                        <Sparkles size={11} className="mr-1" />Sent · pending
                      </Button>
                    ) : (
                      <Button size="sm" onClick={() => setDialog({ kind: 'propose', user: m })} data-testid={`dating-propose-${m.user_id}`} className="bg-amber-500 hover:bg-amber-600 text-black border-0 rounded-full h-8 text-xs font-semibold">
                        <Diamond size={12} className="mr-1" />Propose
                      </Button>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Propose dialog — pick kind (date / engaged / married) + optional sweet note */}
      <ProposeDialog
        open={dialog?.kind === 'propose'}
        target={dialog?.user}
        onClose={() => setDialog(null)}
        onSend={(kind, note) => dialog?.user && sendProposal(dialog.user, kind, note)}
      />

      {/* Report user — Guideline 1.2 mandatory affordance on every real-user surface */}
      <ReportUserDialog
        open={dialog?.kind === 'report'}
        onOpenChange={(v) => {
          if (!v) {
            // On close (success or cancel) skip to next candidate so the
            // reporter doesn't stare at the profile they just flagged.
            if (dialog?.kind === 'report') skip();
            setDialog(null);
          }
        }}
        target={dialog?.user}
      />

      {/* Profile editor */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#161622] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Heart size={18} className="text-rose-400" /> My Dating Profile</DialogTitle></DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Photos */}
            <div>
              <div className="flex items-baseline justify-between">
                <label className="text-xs uppercase tracking-wider text-white/40">Dating photos</label>
                <span className="text-[10px] text-white/40" data-testid="dating-photo-count">{profile.photos.length}/4</span>
              </div>
              <p className="text-[11px] text-white/40 mt-0.5 mb-2">
                Separate from your profile picture. Add up to 4. Hover a photo to set it as your profile picture or remove it.
              </p>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {profile.photos.map((p, i) => (
                  <div key={i} className="aspect-square rounded-xl overflow-hidden bg-white/5 border border-white/10 relative group">
                    <img src={p} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setProfile({ ...profile, photos: profile.photos.filter((_, j) => j !== i) })}
                      data-testid={`dating-photo-remove-${i}`}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-rose-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center transition"
                      title="Remove photo"
                    >
                      <X size={12} />
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await updateProfile({ avatar: p });
                          toast.success('Profile picture updated ✨');
                        } catch {
                          toast.error('Could not set as profile picture');
                        }
                      }}
                      data-testid={`dating-photo-set-avatar-${i}`}
                      className="absolute bottom-1 left-1 right-1 py-1 rounded-md bg-black/70 hover:bg-fuchsia-500 text-white text-[10px] font-semibold opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1"
                      title="Use as profile picture"
                    >
                      <Star size={9} /> Set as profile pic
                    </button>
                  </div>
                ))}
                {profile.photos.length < 4 && (
                  <>
                    <input
                      ref={photoFileRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePhotoUpload}
                      data-testid="dating-photo-input"
                    />
                    <button
                      onClick={() => photoFileRef.current?.click()}
                      data-testid="dating-add-photo-btn"
                      className="aspect-square rounded-xl bg-white/5 border-2 border-dashed border-white/15 hover:border-fuchsia-400/50 flex flex-col items-center justify-center text-white/40 hover:text-white transition"
                    >
                      <Camera size={20} />
                      <span className="text-[10px] mt-1">Add photo</span>
                    </button>
                    <button
                      onClick={() => setAiOpen(true)}
                      data-testid="dating-ai-generate-btn"
                      className="aspect-square rounded-xl bg-gradient-to-br from-fuchsia-500/20 via-purple-500/20 to-cyan-500/20 border-2 border-dashed border-fuchsia-400/50 hover:border-fuchsia-400 flex flex-col items-center justify-center text-fuchsia-200 hover:text-white transition"
                    >
                      <Wand2 size={20} />
                      <span className="text-[10px] mt-1">AI Generate</span>
                    </button>
                  </>
                )}
              </div>
              {profile.photos.length === 0 && (
                <p className="text-[11px] text-amber-300/80 mt-2" data-testid="dating-no-photos-hint">
                  💡 Add at least one dating photo — profiles with photos get 5× more matches.
                </p>
              )}
            </div>

            {/* Gender + Age */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40">Gender</label>
                <Select value={profile.gender} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
                  <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-[#161622] border-white/10 text-white">{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/40">Age</label>
                <Input type="number" min={18} max={99} value={profile.age} onChange={(e) => setProfile({ ...profile, age: e.target.value })} className="mt-1 bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40">Looking for</label>
              <Select value={profile.looking_for} onValueChange={(v) => setProfile({ ...profile, looking_for: v })}>
                <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-[#161622] border-white/10 text-white">{LOOKING_FOR.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40">Bio</label>
              <Textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Tell other travelers about yourself..." className="mt-1 bg-white/5 border-white/10 text-white min-h-[80px]" maxLength={200} />
              <div className="text-[10px] text-white/40 text-right mt-1">{(profile.bio || '').length}/200</div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-white/40">Interests ({profile.interests.length} selected)</label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {INTERESTS.map(i => {
                  const on = profile.interests.includes(i);
                  return (
                    <button key={i} onClick={() => toggleInterest(i)} className={`px-3 py-1 rounded-full text-xs border ${on ? 'bg-fuchsia-500/30 border-fuchsia-400/50 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:bg-white/10'}`}>{i}</button>
                  );
                })}
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
              <div>
                <div className="text-sm font-semibold">Show me in Discover</div>
                <div className="text-[11px] text-white/50">Turn off to hide your profile from others.</div>
              </div>
              <button onClick={() => setProfile({ ...profile, dating_active: !profile.dating_active })} className={`relative w-11 h-6 rounded-full transition-colors ${profile.dating_active ? 'bg-fuchsia-500' : 'bg-white/15'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${profile.dating_active ? 'translate-x-5' : ''}`} />
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={saveProfile} className="w-full bg-gradient-to-r from-fuchsia-500 to-rose-500 text-white border-0"><Sparkles size={14} className="mr-2" />Save profile</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AvatarPicker
        open={aiOpen}
        onOpenChange={setAiOpen}
        currentAvatar={profile.photos[0]}
        onSave={async (url, meta = {}) => {
          const nextPhotos = [...profile.photos, url].slice(0, 4);
          const nextFlags = [...(profile.photos_ai_flags || []), !!meta.isAiGenerated].slice(0, 4);
          setProfile({ ...profile, photos: nextPhotos, photos_ai_flags: nextFlags });
          toast.success(meta.isAiGenerated
            ? 'AI-generated cartoon photo added ✨ (tagged "AI generated")'
            : 'Photo added to your dating profile ✨');
          return true;
        }}
      />

      {/* Dating DM chat */}
      <Dialog open={!!chatMatch} onOpenChange={(o) => !o && setChatMatch(null)}>
        <DialogContent
          className="bg-[#0F0F18] border-white/10 text-white max-w-md p-0 overflow-hidden [&>button.absolute]:hidden"
          data-testid="dating-chat-dialog"
        >
          {chatMatch && (
            <div className="flex flex-col h-[80vh] max-h-[640px]">
              {/* A11y — hidden title + description for screen readers */}
              <DialogTitle className="sr-only">Chat with {chatMatch.displayName}</DialogTitle>
              <DialogDescription className="sr-only">Private chat window with your match. Use block or unblock in the header to manage access.</DialogDescription>
              <div className="px-4 py-3 border-b border-white/5 bg-[#161622] flex items-center gap-3">
                <button onClick={() => setChatMatch(null)} className="text-white/60 hover:text-white p-1" aria-label="Close">
                  <ArrowLeft size={18} />
                </button>
                <Avatar className="w-9 h-9 ring-2 ring-rose-400/50">
                  <AvatarImage src={chatMatch.avatar} />
                  <AvatarFallback>{chatMatch.displayName?.slice(0,2)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{chatMatch.displayName}</div>
                  <div className="text-[11px] text-rose-300 flex items-center gap-1">
                    <Heart size={10} fill="currentColor" /> Matched
                  </div>
                </div>
                <button
                  onClick={chatThread?.blocked_by_me ? unblockChatMatch : blockChatMatch}
                  data-testid={chatThread?.blocked_by_me ? 'dating-unblock-btn' : 'dating-block-btn'}
                  className={`p-1.5 rounded-full transition ${
                    chatThread?.blocked_by_me
                      ? 'text-emerald-300 hover:text-emerald-200 hover:bg-emerald-500/10'
                      : 'text-white/40 hover:text-rose-300 hover:bg-rose-500/10'
                  }`}
                  aria-label={chatThread?.blocked_by_me ? 'Unblock user' : 'Block user'}
                  title={chatThread?.blocked_by_me ? 'Unblock user' : 'Block user'}
                >
                  {chatThread?.blocked_by_me ? <ShieldOff size={16} /> : <Ban size={16} />}
                </button>
              </div>

              {/* Block banners */}
              {chatThread?.blocked_by_me && (
                <div className="px-4 py-2.5 bg-rose-500/10 border-b border-rose-500/25 text-sm text-rose-200 flex items-center gap-3" data-testid="dating-blocked-by-me-banner">
                  <Ban size={15} className="text-rose-300 shrink-0" />
                  <span className="flex-1 text-xs">You blocked {chatMatch.displayName}. They can&apos;t see or message you.</span>
                  <button
                    onClick={unblockChatMatch}
                    data-testid="dating-unblock-banner-btn"
                    className="text-[11px] font-semibold text-emerald-300 hover:text-emerald-200 underline"
                  >
                    Unblock
                  </button>
                </div>
              )}
              {chatThread?.blocked_by_other && !chatThread?.blocked_by_me && (
                <div className="px-4 py-2.5 bg-white/5 border-b border-white/10 text-sm text-white/60 flex items-center gap-2" data-testid="dating-blocked-by-other-banner">
                  <Ban size={15} className="text-white/50 shrink-0" />
                  <span className="text-xs">You can no longer message this user.</span>
                </div>
              )}

              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 scroll-hide">
                {chatLoading && chatMessages.length === 0 && (
                  <div className="text-center text-xs text-white/40 py-4">Loading…</div>
                )}
                {!chatLoading && chatMessages.length === 0 && (
                  <div className="text-center py-8">
                    <Heart size={32} className="mx-auto text-rose-400/50" fill="currentColor" />
                    <div className="text-sm text-white/60 mt-3">Say hi to {chatMatch.displayName} 💕</div>
                    <div className="text-[11px] text-white/40 mt-1">Be kind. Break the ice with an emoji 👋</div>
                  </div>
                )}
                {chatMessages.map(m => {
                  const mine = m.sender_id === user?.user_id;
                  const isImage = typeof m.text === 'string' && m.text.startsWith('data:image/');
                  if (m.isDateInvite && m.dateInvite) {
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <DateInviteCard msg={m} mine={mine} onUpdate={updateInviteMsg} />
                      </div>
                    );
                  }
                  if (m.isGift && m.gift) {
                    return (
                      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className="max-w-[78%] px-3 py-2.5 rounded-2xl text-sm flex items-center gap-3"
                          style={{ background: 'linear-gradient(135deg, rgba(251,191,36,0.2), rgba(244,114,182,0.15))', border: '1px solid rgba(251,191,36,0.4)' }}
                          data-testid="dating-gift-msg"
                        >
                          <div className="text-3xl">{m.gift.emoji}</div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1 text-amber-200 font-semibold text-xs">
                              <Sparkles size={11} /> {m.gift.name}
                            </div>
                            <div className="text-[10px] text-amber-100/70 flex items-center gap-1 mt-0.5">
                              <Star size={8} fill="currentColor" />{m.gift.price?.toLocaleString?.() || m.gift.price}
                              <span className="text-white/40 ml-2">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] ${isImage ? 'p-1.5' : 'px-3 py-2'} rounded-2xl text-sm ${
                        mine ? 'bg-gradient-to-br from-rose-500 to-pink-600 text-white' : 'bg-white/8 text-white/90'
                      }`}>
                        {isImage
                          ? <img src={m.text} alt="" className="rounded-xl max-w-[240px] max-h-[280px] object-cover block" />
                          : m.text}
                        <div className={`text-[10px] mt-1 ${isImage ? 'px-2 pb-1' : ''} ${mine ? 'text-white/70' : 'text-white/40'}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <ChatComposer
                testIdPrefix="dating-dm"
                placeholder={
                  chatThread?.blocked_by_me ? 'Unblock to send messages…'
                  : chatThread?.blocked_by_other ? 'You can no longer message this user'
                  : `Message ${chatMatch.displayName}…`
                }
                disabled={!!(chatThread?.blocked_by_me || chatThread?.blocked_by_other)}
                onSend={sendChat}
                gifts={gifts}
                onSendGift={sendGift}
                dateInvites={DATE_INVITE_PRESETS}
                onSendDateInvite={sendDateInvite}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      {fly && <GiftFly emoji={fly.emoji} name={fly.name} onDone={() => setFly(null)} />}
    </div>
  );
}

const PROPOSAL_KINDS = [
  { id: 'date',    label: 'Dating',     emoji: '💕', tag: 'New beginning', color: 'from-rose-500 to-pink-600' },
  { id: 'engaged', label: 'Engaged',    emoji: '💍', tag: 'Promise ring',  color: 'from-amber-400 to-orange-500' },
  { id: 'married', label: 'Married',    emoji: '💒', tag: 'Forever yours', color: 'from-fuchsia-500 to-purple-600' },
];

function ProposeDialog({ open, target, onClose, onSend }) {
  const [kind, setKind] = React.useState('date');
  const [note, setNote] = React.useState('');
  React.useEffect(() => { if (open) { setKind('date'); setNote(''); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md" data-testid="dating-propose-dialog">
        <DialogHeader><DialogTitle className="font-display">Propose to {target?.displayName || '…'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {PROPOSAL_KINDS.map(k => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              data-testid={`dating-propose-kind-${k.id}`}
              className={`rounded-xl p-3 text-center transition border ${kind === k.id ? 'border-amber-400 bg-gradient-to-br ' + k.color : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
            >
              <div className="text-3xl">{k.emoji}</div>
              <div className="text-xs font-semibold mt-1">{k.label}</div>
              <div className="text-[9px] uppercase tracking-wider text-white/60 mt-0.5">{k.tag}</div>
            </button>
          ))}
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-white/40">Sweet note (optional)</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Say something heartfelt…"
            maxLength={240}
            rows={2}
            data-testid="dating-propose-note"
            className="mt-1 bg-white/5 border-white/10 text-white text-sm min-h-[60px]"
          />
        </div>
        <DialogFooter className="mt-3">
          <Button
            onClick={() => onSend?.(kind, note.trim() || null)}
            data-testid="dating-propose-send"
            className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-black border-0 rounded-full font-semibold"
          >
            <Sparkles size={14} className="mr-2" />Send {PROPOSAL_KINDS.find(k => k.id === kind)?.emoji} proposal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
